import type { CSSProperties, ReactNode } from 'react';

import { cn } from '@/lib/cn';
import styles from './GuideOrbButton.module.css';

import { GUIDE_ORB_BLUE, GUIDE_ORB_MOTION_CSS } from '../../../../shared/conversation/guide-orb';
import type { SubagentAvatarVariant, GuideOrbVariant } from '../../../../shared/conversation/guide-orb';
export { SUBAGENT_AVATAR_VARIANTS, getSubagentAvatarVariant } from '../../../../shared/conversation/guide-orb';
export type { SubagentAvatarVariant, GuideOrbVariant } from '../../../../shared/conversation/guide-orb';

/** 所有角色共用原蓝球眼部关键帧，配件属于头部，不能绑到单只眼睛上。 */
function CharacterBody({ variant }: { variant: SubagentAvatarVariant }) {
  return <svg className={styles.characterBody} viewBox="0 0 15 15" fill={GUIDE_ORB_BLUE} focusable="false">
    {variant === 'square' ? <rect x="1" y="1" width="13" height="13" rx="2.5" />
      : variant === 'cloud' ? <path d="M3.1 11.9C.2 11.9-.1 8.4 2.1 7.7C1.5 5.3 3.1 3.5 5.2 3.7C6.5 1.4 10.1 1.7 11 4C13.6 3.6 14.7 6 13.5 7.7C16 10 13.7 12.8 11.2 12.1C8.7 14.1 5.3 13.7 3.1 11.9Z" />
      : variant === 'diamond' ? <path d="M7.1.9Q7.5.5 7.9.9L14 7.1Q14.4 7.5 14 7.9L7.9 14.1Q7.5 14.5 7.1 14.1L1 7.9Q.6 7.5 1 7.1Z" />
      : variant === 'triangle-glasses' ? <path d="M7.05 1.3Q7.5.65 7.95 1.3C10.2 4.1 12.15 7.7 12.4 12.8Q12.5 13.7 7.5 13.7Q2.5 13.7 2.6 12.8C2.85 7.7 4.8 4.1 7.05 1.3Z" />
      : <circle cx="7.5" cy="7.5" r={variant === 'headphones' ? 5.7 : 6.5} />}
  </svg>;
}

function CharacterAccessory({ variant }: { variant: SubagentAvatarVariant }) {
  if (variant === 'headphones') return <svg className={styles.accessory} viewBox="0 0 15 15" fill="none" focusable="false">
    <path d="M1.4 8V7a6.1 6.1 0 0 1 12.2 0v1" stroke="#111316" strokeWidth=".85" />
    <rect x=".3" y="6.1" width="2" height="4" rx=".9" fill="#111316" />
    <rect x="12.7" y="6.1" width="2" height="4" rx=".9" fill="#111316" />
  </svg>;
  if (variant !== 'round-glasses' && variant !== 'triangle-glasses') return null;
  return <svg className={styles.accessory} viewBox="0 0 15 15" fill="none" stroke="#153449" strokeWidth=".3" focusable="false">
    {variant === 'round-glasses' ? <><circle cx="6.3" cy="7.25" r="1.1" /><circle cx="8.7" cy="7.25" r="1.1" /></>
      : <><rect x="5.2" y="5.55" width="2.2" height="3.05" rx=".55" /><rect x="7.6" y="5.55" width="2.2" height="3.05" rx=".55" /></>}
    <path d="M7.4 7.1h.2M4.85 7.1h.35M9.8 7.1h.35" strokeLinecap="round" />
  </svg>;
}

/** 入口与加载态共用的柔雾蓝球；角色变体保留各自的几何皮肤。 */
export function GuideOrb({ size = 56, variant = 'orb', animated = true }: {
  size?: number;
  variant?: GuideOrbVariant;
  animated?: boolean;
}) {
  const wearsGlasses = variant === 'round-glasses' || variant === 'triangle-glasses';
  // 直接在眼睛图层定义镜片区域，不依赖可能在独立渲染时重复的 SVG ID。
  const lensClip = variant === 'round-glasses'
    ? 'path("M5.35 7.25a.95.95 0 1 0 1.9 0a.95.95 0 1 0-1.9 0Z M7.75 7.25a.95.95 0 1 0 1.9 0a.95.95 0 1 0-1.9 0Z")'
    : 'path("M5.75 5.7H6.85Q7.25 5.7 7.25 6.1V8.05Q7.25 8.45 6.85 8.45H5.75Q5.35 8.45 5.35 8.05V6.1Q5.35 5.7 5.75 5.7Z M8.15 5.7H9.25Q9.65 5.7 9.65 6.1V8.05Q9.65 8.45 9.25 8.45H8.15Q7.75 8.45 7.75 8.05V6.1Q7.75 5.7 8.15 5.7Z")';
  return (
    <span className={cn(styles.mark, variant !== 'orb' && styles.character, !animated && styles.still)} style={{ '--guide-orb-size': size } as CSSProperties} aria-hidden="true" data-guide-orb={variant} data-guide-orb-motion={String(animated)}>
      <style>{GUIDE_ORB_MOTION_CSS}</style>
      {variant === 'orb' && <span className={styles.surface} />}
      <span className={styles.face}>
        {variant !== 'orb' && <CharacterBody variant={variant} />}
        {wearsGlasses ? <span className={styles.eyewearRig} data-guide-orb-rig>
          <span className={styles.head} style={{ clipPath: lensClip }}><i data-guide-orb-eye="left" /><i data-guide-orb-eye="right" /></span>
          <CharacterAccessory variant={variant} />
        </span> : <>
          <span className={styles.head}><i data-guide-orb-eye="left" /><i data-guide-orb-eye="right" /></span>
          {variant !== 'orb' && <CharacterAccessory variant={variant} />}
        </>}
      </span>
    </span>
  );
}

export function GuideOrbLoading({ label, fill = 'parent', className, size = 48 }: {
  label: ReactNode;
  fill?: 'parent' | 'screen';
  className?: string;
  size?: number;
}) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={cn(
      'flex w-full flex-col items-center justify-center gap-4 px-6 py-8 text-center',
      fill === 'screen' ? 'min-h-screen' : 'h-full min-h-0 flex-1',
      className,
    )}>
      <GuideOrb size={size} />
      <span className="text-sm font-medium leading-5 text-slate-600">{label}</span>
    </div>
  );
}
